import LogRocket from 'logrocket';

import { PREFIX_STATES as INSURANCES_STATES_PREFIX } from '@modules/insurances/types';
import { PREFIX as LEADS_PREFIX } from '@modules/leads/types';
import { PREFIX as REFERRING_PHYSICIANS_PREFIX } from '@modules/referring-physician/types';
import { PREFIX as WAIT_LIST_PREFIX } from '@modules/wait-list/types';

import { ReducerType } from './reducer';

const LARGE_PAYLOAD_ACTIONS = [
  WAIT_LIST_PREFIX,
  REFERRING_PHYSICIANS_PREFIX,
  INSURANCES_STATES_PREFIX,
  `${LEADS_PREFIX}/LIST`,
  `${LEADS_PREFIX}/RETRIEVE`,
];

const truncateArray = <T>(array: T[] | undefined, limit: number): T[] =>
  array?.slice(0, limit) || [];

const sanitizeLeads = (leads: ReducerType['leads']) =>
  leads
    ? {
        _sanitized: true,
        itemsCount: leads.items?.length || 0,
        hasItem: !!leads.item,
        historiesCount: leads.histories?.items?.length || 0,
      }
    : undefined;

const sanitizeReports = (reports: ReducerType['reports']) =>
  reports
    ? {
        _sanitized: true,
        hasGraph: !!reports.graph,
        hasReports: !!reports.reports,
      }
    : undefined;

const sanitizeDashboard = (dashboard: ReducerType['dashboard']) =>
  dashboard
    ? {
        _sanitized: true,
        widgetsCount: Object.keys(dashboard.dataWidgets || {}).length,
      }
    : undefined;

const sanitizeEhrSync = (ehrSync: ReducerType['ehrSync']) =>
  ehrSync
    ? {
        ...ehrSync,
        list: { ...ehrSync.list, items: truncateArray(ehrSync.list?.items, 5) },
        modalMatches: { items: [] },
      }
    : undefined;

const sanitizeUsers = (users: ReducerType['users']) =>
  users
    ? { ...users, items: truncateArray(users.items, 10), dropdown: [] }
    : undefined;

const sanitizeList = (list: ReducerType['list']) =>
  list ? { _sanitized: true, itemsCount: list.items?.length || 0 } : undefined;

const sanitizeModuleWithItems = <T extends { items?: unknown[] }>(
  module: T | undefined,
  limit = 10,
) =>
  module ? { ...module, items: truncateArray(module.items, limit) } : undefined;

const sanitizeState = (state: ReducerType) => ({
  ...state,
  leads: sanitizeLeads(state.leads),
  reports: sanitizeReports(state.reports),
  dashboard: sanitizeDashboard(state.dashboard),
  ehrSync: sanitizeEhrSync(state.ehrSync),
  users: sanitizeUsers(state.users),
  form: state.form ? { _sanitized: true } : undefined,
  clinics: sanitizeModuleWithItems(state.clinics),
  groups: sanitizeModuleWithItems(state.groups),
  locations: sanitizeModuleWithItems(state.locations),
  insurances: sanitizeModuleWithItems(state.insurances),
  sources: sanitizeModuleWithItems(state.sources),
  campaigns: sanitizeModuleWithItems(state.campaigns),
  scripts: state.scripts ? { _sanitized: true } : undefined,
  list: sanitizeList(state.list),
  waitList: state.waitList ? { _sanitized: true } : undefined,
  referringPhysicians: state.referringPhysicians
    ? { _sanitized: true }
    : undefined,
});

const logRocketMiddleware = LogRocket.reduxMiddleware({
  stateSanitizer: (state) => {
    if (!state) return state;
    return sanitizeState(state as ReducerType);
  },

  actionSanitizer: (action) => {
    const actionType = action.type as string;

    if (LARGE_PAYLOAD_ACTIONS.some((prefix) => actionType?.includes(prefix))) {
      return { type: actionType, payload: { _sanitized: true } };
    }

    if (action.payload?.items?.length > 20) {
      return {
        type: actionType,
        payload: { _sanitized: true, itemsCount: action.payload.items.length },
      };
    }

    return action;
  },
});

export default logRocketMiddleware;
